import { useEffect, useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowUpRight, Phone, Mail, MapPin, Sparkles, ChevronRight, Building2, Home } from "lucide-react";
import { BRAND } from "@/config/brand";
import { DFW_COUNTIES, getCountyBySlug } from "@/config/dfw-master";
import { DFW_CITIES } from "@/config/cities";
import PlugitAIChat from "@/pages/PlugitAIChat";

const C = BRAND.colors;

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function upsertCanonical(href) {
  let el = document.querySelector("link[rel='canonical']");
  if (!el) { el = document.createElement("link"); el.rel = "canonical"; document.head.appendChild(el); }
  el.href = href;
}
function upsertLD(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

export default function CountyPage() {
  const { county: slug } = useParams();
  const county = useMemo(() => getCountyBySlug(slug), [slug]);
  const linkedCities = useMemo(
    () => county ? county.topCities.map((s) => DFW_CITIES.find((c) => c.slug === s)).filter(Boolean) : [],
    [county]
  );

  useEffect(() => {
    if (!county) return;
    const origin = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://plugitai.com");
    const url = `${origin}/tx/county/${county.slug}`;
    const title = `AI Websites & Sales Agents in ${county.name}, TX — 100+ Local Cities | PlugitAI`;
    const desc = `AI-powered websites and AI sales agents for small businesses across ${county.name}, Texas. Serving ${county.topCities.slice(0, 4).map((s) => (DFW_CITIES.find(c => c.slug === s)?.name) || s).join(", ")}, and more. Lock in your build with $299.99.`;
    document.title = title;
    upsertMeta("description", desc);
    upsertMeta("robots", "index,follow,max-image-preview:large,max-snippet:-1");
    upsertMeta("og:title", title, true);
    upsertMeta("og:description", desc, true);
    upsertMeta("og:type", "website", true);
    upsertMeta("og:url", url, true);
    upsertMeta("og:image", `${origin}/hero-ai-twin.jpg`, true);
    upsertMeta("twitter:card", "summary_large_image");
    upsertCanonical(url);

    upsertLD("ld-county-service", {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: `AI website design and AI sales agents in ${county.name}, TX`,
      provider: {
        "@type": "Organization",
        name: "PlugitAI",
        url: origin,
        telephone: "+12148155966",
        email: "cgarciathanksyou@gmail.com",
      },
      areaServed: { "@type": "AdministrativeArea", name: county.name },
      offers: { "@type": "Offer", priceCurrency: "USD", price: "299.99", description: "$299.99 project lock-in. Full builds typically under $1,800." },
      description: desc,
    });
    upsertLD("ld-county-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin },
        { "@type": "ListItem", position: 2, name: "Texas", item: `${origin}/tx` },
        { "@type": "ListItem", position: 3, name: county.name, item: url },
      ],
    });
    upsertLD("ld-county-place", {
      "@context": "https://schema.org",
      "@type": "AdministrativeArea",
      name: county.name,
      containedInPlace: { "@type": "State", name: "Texas" },
      geo: { "@type": "GeoCoordinates", latitude: county.lat, longitude: county.lng },
    });

    return () => {
      ["ld-county-service", "ld-county-breadcrumb", "ld-county-place"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [county]);

  if (!county) return <Navigate to="/tx" replace />;

  const accent = county.color;

  return (
    <div className="min-h-screen w-full overflow-x-hidden"
         style={{ background: C.ink, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}
         data-testid={`county-page-${county.slug}`}>
      <TopNav />

      <div aria-hidden className="pointer-events-none fixed inset-0 z-0"
           style={{
             background: `radial-gradient(1200px 700px at 15% -10%, ${accent}22, transparent 60%),
                          radial-gradient(900px 600px at 90% 15%, ${accent}18, transparent 60%),
                          radial-gradient(1400px 900px at 60% 110%, ${C.blueDeep}44, transparent 70%)`,
           }} />

      <section className="relative z-10 pt-28 md:pt-36 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <nav className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 mb-8 flex-wrap" style={{ color: C.textFade }}>
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={12} />
            <Link to="/tx" className="hover:text-white">Texas</Link>
            <ChevronRight size={12} />
            <span style={{ color: C.text }}>{county.name}</span>
          </nav>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-[0.2em] mb-8"
               style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}55` }}>
            <MapPin size={12} />
            County seat: {county.seat} · Population {county.population}
          </div>

          <h1 className="font-black tracking-[-0.02em] leading-[0.98]"
              style={{ fontSize: "clamp(2.4rem, 5.4vw, 5.4rem)", color: "white" }}
              data-testid="county-h1">
            AI websites for{" "}
            <span style={{
              backgroundImage: `linear-gradient(135deg, ${accent} 0%, #ffffffAA 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              filter: `drop-shadow(0 0 24px ${accent}66)`,
            }}>
              {county.name}
            </span>
            .
          </h1>

          <p className="mt-8 max-w-[820px] text-lg md:text-xl leading-relaxed" style={{ color: C.textDim }}>
            {county.intro}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href="tel:+12148155966"
               className="inline-flex items-center gap-2 px-7 h-14 rounded-full font-semibold text-base"
               style={{
                 background: `linear-gradient(135deg, ${accent} 0%, ${C.greenBright} 100%)`,
                 color: "white",
                 boxShadow: `0 24px 60px -20px ${accent}aa`,
               }}>
              <Phone size={14} /> Talk to Kit — (214) 815-5966
            </a>
            <button type="button"
                    onClick={() => document.querySelector("[data-testid=plugitai-chat-open]")?.click()}
                    className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base border hover:bg-white/5"
                    style={{ borderColor: `${accent}77`, color: "white" }}>
              <Sparkles size={14} /> Chat with the AI now
            </button>
          </div>
        </div>
      </section>

      {/* Cities we serve */}
      <section className="relative z-10 py-16 md:py-24 border-t"
               style={{ borderColor: C.line, background: "rgba(11,21,36,0.72)" }}
               data-testid="county-cities">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accent }}>
            Cities we serve
          </div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02] mb-10"
              style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", color: "white" }}>
            {county.name} cities with AI-powered PlugitAI websites.
          </h2>

          {linkedCities.length === 0 ? (
            <p className="text-sm md:text-base" style={{ color: C.textDim }}>
              City pages are rolling out. Call Kit for a custom build anywhere in {county.name}.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {linkedCities.map((c) => (
                <Link key={c.slug} to={`/tx/${c.slug}`}
                      className="group rounded-2xl p-5 md:p-6 border transition-all hover:-translate-y-1"
                      style={{
                        borderColor: `${accent}33`,
                        background: "rgba(17,24,39,0.55)",
                        backdropFilter: "blur(10px)",
                      }}
                      data-testid={`county-city-${c.slug}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Home size={13} style={{ color: accent }} />
                    <div className="font-black text-white text-lg md:text-xl">{c.name}</div>
                  </div>
                  <div className="text-xs uppercase tracking-[0.18em] mb-3" style={{ color: C.textFade }}>
                    Pop {c.population} · {c.zip}
                  </div>
                  <p className="text-sm leading-snug" style={{ color: C.textDim }}>{c.hook}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: accent }}>
                    See {c.name} page → <ArrowUpRight size={12} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Subdivisions */}
      <section className="relative z-10 py-16 md:py-20" data-testid="county-subdivisions">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accent }}>
            Master-planned communities we serve
          </div>
          <h3 className="font-black text-white mb-6" style={{ fontSize: "clamp(1.4rem, 2.4vw, 1.9rem)" }}>
            {county.name} subdivisions & neighborhoods.
          </h3>
          <div className="flex flex-wrap gap-2">
            {county.topSubdivisions.map((sub) => (
              <span key={sub}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
                    style={{
                      background: `${accent}12`,
                      borderColor: `${accent}55`,
                      color: "white",
                    }}
                    data-testid={`county-sub-${sub.replace(/\s+/g, "-").toLowerCase()}`}>
                <Building2 size={12} style={{ color: accent }} /> {sub}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 pb-24 md:pb-36">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="relative rounded-3xl p-8 md:p-14 border overflow-hidden"
               style={{
                 background: `linear-gradient(135deg, ${accent}22 0%, ${C.greenBright}12 100%), ${C.inkSoft}`,
                 borderColor: `${accent}66`,
                 boxShadow: `0 80px 200px -60px ${accent}66`,
               }}
               data-testid="county-final-cta">
            <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accent }}>
              Lock in your build
            </div>
            <h3 className="font-black tracking-[-0.02em] leading-[1.02]"
                style={{ fontSize: "clamp(1.8rem, 3.6vw, 2.8rem)", color: "white" }}>
              Ready to give your {county.name} business an AI twin?
            </h3>
            <p className="mt-4 max-w-[640px] text-base md:text-lg leading-relaxed text-white/85">
              Lock your project in with{" "}
              <span className="font-black" style={{ color: accent }}>$299.99</span>{" "}
              and Kit will personally design your website, AI sales agent, and lead-response system.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="tel:+12148155966"
                 className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base"
                 style={{
                   background: `linear-gradient(135deg, ${accent} 0%, ${C.greenBright} 100%)`,
                   color: "white",
                   boxShadow: `0 24px 60px -20px ${accent}aa`,
                 }}>
                <Phone size={14} /> Call (214) 815-5966
              </a>
              <a href={`mailto:cgarciathanksyou@gmail.com?subject=${encodeURIComponent(`AI build in ${county.name}`)}`}
                 className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base border"
                 style={{ borderColor: `${accent}66`, color: "white" }}>
                <Mail size={14} /> Email me
              </a>
              <Link to="/tx"
                    className="inline-flex items-center gap-2 px-4 h-11 rounded-full font-semibold text-sm border"
                    style={{ borderColor: C.line, color: C.textDim }}>
                See all cities <ArrowUpRight size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <PlugitAIChat />
    </div>
  );
}

function TopNav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50"
            style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${C.line}` }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-10 md:h-12 w-auto" />
          <span className="hidden xl:inline-block whitespace-nowrap text-[10px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
            Powered by {BRAND.engineName}
          </span>
        </Link>
        <a href="tel:+12148155966"
           className="inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm"
           style={{ background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`, color: "white" }}>
          <Phone size={14} /> (214) 815-5966
        </a>
      </div>
    </header>
  );
}
